import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import type { LineCitation } from '../../services/ai/citations/citationTypes'
import { LINE_CITATION_ICON } from '../../services/ai/citations/citationTypes'
import { useCitationDispatch, useCitationSurface } from './citationDispatch'
import { CitationExcerptCard } from './CitationExcerptCard'
import { Pressable } from '../Common/Pressable'
import { CitationExcerptSheet } from './CitationExcerptSheet'
import { useNls } from '../../nls/nlsProvider'

const SUPERSCRIPTS = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹']
const toSuperscript = (num: number) => num.toString().split('').map((digit) => SUPERSCRIPTS[Number.parseInt(digit, 10)] ?? digit).join('')

export const CiteChip = ({
  citation,
  index,
  isRevealed,
}: {
  citation: LineCitation
  index: number
  isRevealed: boolean
}) => {
  const { t } = useNls()
  const surface = useCitationSurface()
  const dispatch = useCitationDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const uid = useId()
  const Icon = LINE_CITATION_ICON[citation.sourceKind] ?? LinkRoundedIcon
  const label = toSuperscript(index)

  const openViewer = useCallback(() => {
    if (buttonRef.current) setAnchor(buttonRef.current.getBoundingClientRect())
    setIsOpen(true)
  }, [])
  const closeViewer = useCallback(() => setIsOpen(false), [])

  const handleClick = useCallback(() => {
    if (surface === 'mobile') {
      openViewer()
      return
    }
    // Desktop click primary action = open the source.
    const routed = dispatch(citation)
    if (!routed) openViewer()
  }, [surface, dispatch, citation, openViewer])

  const handleKey = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick()
    }
    if (event.key === 'Escape') closeViewer()
  }, [handleClick, closeViewer])

  useEffect(() => {
    if (!isOpen) return
    const onDocClick = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return
      if (buttonRef.current?.contains(event.target)) return
      closeViewer()
    }
    const onDocKey = (event: KeyboardEvent) => { if (event.key === 'Escape') closeViewer() }
    window.addEventListener('mousedown', onDocClick)
    window.addEventListener('keydown', onDocKey)
    return () => {
      window.removeEventListener('mousedown', onDocClick)
      window.removeEventListener('keydown', onDocKey)
    }
  }, [isOpen, closeViewer])

  const hoverOpen = useCallback(() => {
    if (surface !== 'desktop') return
    openViewer()
  }, [surface, openViewer])

  const chipStyle = useMemo<React.CSSProperties>(() => ({
    opacity: isRevealed ? 1 : 0,
    transform: isRevealed ? 'translateY(0) scale(1)' : 'translateY(-2px) scale(0.85)',
    transition: 'opacity 160ms ease, transform 160ms ease',
    pointerEvents: isRevealed ? 'auto' : 'none',
  }), [isRevealed])

  return (
    <>
      <Pressable
        ref={buttonRef}
        type="button"
        className={`lineCiteChip lineCiteChip--${citation.sourceKind}`}
        aria-describedby={isOpen ? `${uid}-viewer` : undefined}
        aria-label={t(surface === 'mobile' ? 'NLSAiCitationChipMobileAria' : 'NLSAiCitationChipDesktopAria', { index, title: citation.title })}
        onClick={handleClick}
        onMouseEnter={hoverOpen}
        onMouseLeave={() => { if (surface === 'desktop') closeViewer() }}
        onKeyDown={handleKey}
        style={chipStyle}
        data-cite-index={index}
      >
        <span className="lineCiteChip__glyph" aria-hidden="true"><Icon fontSize="inherit" /></span>
        <span className="lineCiteChip__index">{label}</span>
      </Pressable>
      {isOpen && surface === 'desktop' && anchor && (
        <CitationExcerptCard
          id={`${uid}-viewer`}
          citation={citation}
          anchor={anchor}
          onClose={closeViewer}
          onOpenSource={() => { dispatch(citation); closeViewer() }}
        />
      )}
      {isOpen && surface === 'mobile' && (
        <CitationExcerptSheet
          id={`${uid}-viewer`}
          citation={citation}
          onClose={closeViewer}
          onOpenSource={() => { dispatch(citation); closeViewer() }}
        />
      )}
    </>
  )
}
